import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, TextField, Button,
    Select, MenuItem, FormControl, InputLabel, Divider,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    CircularProgress, Alert,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const API = '';

const PersonAutocomplete = ({ label, value, onChange, exclude }) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        if (inputValue.length < 2) { setOptions([]); return; }
        axios.get(`${API}/api/people?search=${encodeURIComponent(inputValue)}`)
            .then(res => setOptions(res.data.filter(p => p.id_person !== exclude)))
            .catch(() => setOptions([]));
    }, [inputValue, exclude]);

    return (
        <Autocomplete
            options={options}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            onInputChange={(_, newInput, reason) => {
                if (reason === 'input') setInputValue(newInput);
            }}
            filterOptions={x => x}
            getOptionLabel={opt => opt ? `${opt.firstname} ${opt.lastname}`.trim() : ''}
            isOptionEqualToValue={(opt, val) => opt.id_person === val.id_person}
            renderInput={(params) => <TextField {...params} label={label} size="small" />}
            noOptionsText={inputValue.length < 2 ? 'Escribí al menos 2 letras' : 'Sin resultados'}
        />
    );
};

const PersonForm = ({ isNew }) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        firstname: '', lastname: '', sex: '',
        birthday: '', decease: '',
        city: '', province: '', country: '',
        profession: '', observations: '',
    });
    const [father, setFather] = useState(null);
    const [mother, setMother] = useState(null);
    const [spouse, setSpouse] = useState(null);
    const [marriage, setMarriage] = useState('');
    const [originalSpouseId, setOriginalSpouseId] = useState(null);

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [personId] = useState(id ? parseInt(id) : null);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        axios.get(`${API}/api/people/${id}`)
            .then(res => {
                const p = res.data;
                setForm({
                    firstname: p.firstname || '',
                    lastname: p.lastname || '',
                    sex: p.sex || '',
                    birthday: p.birthday || '',
                    decease: p.decease || '',
                    city: p.city || '',
                    province: p.province || '',
                    country: p.country || '',
                    profession: p.profession || '',
                    observations: p.observations || '',
                });
                if (p.id_father) setFather({ id_person: p.id_father, firstname: p.father || '', lastname: '' });
                if (p.id_mother) setMother({ id_person: p.id_mother, firstname: p.mother || '', lastname: '' });
                if (p.spouse_id) {
                    setSpouse({ id_person: p.spouse_id, firstname: p.spouse_firstname || '', lastname: p.spouse_lastname || '' });
                    setOriginalSpouseId(p.spouse_id);
                    setMarriage(p.marriage || '');
                }
                setLoading(false);
            })
            .catch(() => { setError('Error al cargar la persona.'); setLoading(false); });
    }, [id, isNew]);

    const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                id_father: father?.id_person || null,
                id_mother: mother?.id_person || null,
                father: father ? `${father.firstname} ${father.lastname}`.trim() : null,
                mother: mother ? `${mother.firstname} ${mother.lastname}`.trim() : null,
            };

            let savedId = personId;
            if (isNew) {
                const res = await axios.post(`${API}/api/people`, payload);
                savedId = res.data.id;
            } else {
                await axios.put(`${API}/api/people/${id}`, payload);
            }

            if (spouse) {
                const isFemale = form.sex === 'F';
                await axios.post(`${API}/api/couples`, {
                    id_husband: isFemale ? spouse.id_person : savedId,
                    id_wife: isFemale ? savedId : spouse.id_person,
                    marriage: marriage || null,
                });
            } else if (!spouse && originalSpouseId) {
                await axios.delete(`${API}/api/couples/${savedId}`);
            }

            navigate(`/tree/${savedId}`);
        } catch (err) {
            setError(err.response?.data?.error || 'Error al guardar.');
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await axios.delete(`${API}/api/couples/${id}`).catch(() => {});
            await axios.delete(`${API}/api/people/${id}`);
            navigate('/');
        } catch (err) {
            setError('Error al eliminar.');
            setSaving(false);
            setDeleteDialog(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ maxWidth: 680, mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                {isNew ? 'Nueva persona' : 'Editar persona'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Datos personales
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Nombre" value={form.firstname} onChange={handleChange('firstname')} size="small" fullWidth />
                        <TextField label="Apellido" value={form.lastname} onChange={handleChange('lastname')} size="small" fullWidth />
                    </Box>
                    <FormControl size="small" sx={{ maxWidth: 180 }}>
                        <InputLabel>Sexo</InputLabel>
                        <Select value={form.sex} label="Sexo" onChange={handleChange('sex')}>
                            <MenuItem value="M">Masculino</MenuItem>
                            <MenuItem value="F">Femenino</MenuItem>
                        </Select>
                    </FormControl>

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Fechas
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Nacimiento" type="date" value={form.birthday} onChange={handleChange('birthday')} size="small" fullWidth InputLabelProps={{ shrink: true }} />
                        <TextField label="Fallecimiento" type="date" value={form.decease} onChange={handleChange('decease')} size="small" fullWidth InputLabelProps={{ shrink: true }} />
                    </Box>

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Lugar
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Ciudad" value={form.city} onChange={handleChange('city')} size="small" fullWidth />
                        <TextField label="Provincia" value={form.province} onChange={handleChange('province')} size="small" fullWidth />
                        <TextField label="País" value={form.country} onChange={handleChange('country')} size="small" fullWidth />
                    </Box>

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Otros
                    </Typography>
                    <TextField label="Profesión" value={form.profession} onChange={handleChange('profession')} size="small" fullWidth />
                    <TextField label="Observaciones" value={form.observations} onChange={handleChange('observations')} size="small" fullWidth multiline rows={3} />

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Padres
                    </Typography>
                    <PersonAutocomplete label="Padre" value={father} onChange={setFather} exclude={personId} />
                    <PersonAutocomplete label="Madre" value={mother} onChange={setMother} exclude={personId} />

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Cónyuge
                    </Typography>
                    <PersonAutocomplete label="Cónyuge" value={spouse} onChange={setSpouse} exclude={personId} />
                    {spouse && (
                        <TextField
                            label="Fecha de casamiento"
                            type="date"
                            value={marriage}
                            onChange={e => setMarriage(e.target.value)}
                            size="small"
                            sx={{ maxWidth: 220 }}
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                {!isNew && (
                    <Button variant="outlined" color="error" onClick={() => setDeleteDialog(true)} disabled={saving}>
                        Eliminar
                    </Button>
                )}
                <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                    <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : 'Guardar'}
                    </Button>
                </Box>
            </Box>

            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que querés eliminar a {form.firstname} {form.lastname}? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" disabled={saving}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PersonForm;
